/**
 * Helpers for finite-slider and infinite-slider.
 */

import { each, max, map } from 'lodash';
import { safeParseJSON } from './utils';

declare const jQuery: JQueryStatic;
const $ = jQuery;

const config = Object.freeze({
	dfWait: 4000,
	minWait: 1000,
	minItemsPerGroup: 1,
	dfItemsPerGroup: [
		{ minWindowWidth: 500, nItems: 2 },
		{ minWindowWidth: 768, nItems: 3 },
		{ minWindowWidth: 1200, nItems: 4 }
	],
});

export interface ItemsPerGroupSetting {
	minWindowWidth: number;
	nItems: number;
}

export interface ItemWidthMultiplier {
	minWindowWidth: number;
	multiplier: number;
}

export function getWaitValueFromWrapAttr($wrap: JQuery, allowNoWait: boolean = true, dfWait: number = config.dfWait, minWait: number = config.minWait): number | undefined {
	const strValue: string = $wrap.attr('data-auto-slide-wait') || '';
	if (!strValue) {
		return allowNoWait ? undefined : dfWait;
	}
	const numValue: number = parseFloat(strValue);
	if (numValue <= 0) {
		return allowNoWait ? undefined : dfWait;
	}
	return numValue ? max([minWait, numValue * 1000]) : (allowNoWait ? undefined : dfWait);
}

export function getItemsPerGroupDataFromWrapAttr($wrap: JQuery, dfItemsPerGroup = config.dfItemsPerGroup): ItemsPerGroupSetting[] {
	const attrVal: string = $wrap.attr('data-items-per-group') || '';
	if (!attrVal) {
		return dfItemsPerGroup;
	}
	const data = safeParseJSON(attrVal);
	if (!data?.length) {
		return dfItemsPerGroup;
	}
	return map(data, (item: number[]) => ({ minWindowWidth: item[0], nItems: item[1] }));
}

export function findHighestItemsPerGroupValue(settings: ItemsPerGroupSetting[]): number | undefined {
	let highest: number | undefined;
	each(settings, (setting: ItemsPerGroupSetting) => {
		if (highest === undefined || highest < setting.nItems) {
			highest = setting.nItems;
		}
	});
	return highest;
}

export function getItemsPerGroup($window: JQuery<Window & typeof globalThis>, itemsPerGroupSettings: ItemsPerGroupSetting[]): number {
	let itemsPerGroup: number = config.minItemsPerGroup;
	const w: number = $window.outerWidth() || 0;
	each(itemsPerGroupSettings, cfg => {
		if (w < cfg.minWindowWidth) {
			return false;
		}
		itemsPerGroup = cfg.nItems;
	});
	return itemsPerGroup;
}

export function itemsPerGroupObjectToHtmlAttr(itemsPerGroup: ItemsPerGroupSetting[]): string {
	return map(itemsPerGroup, (setting: ItemsPerGroupSetting) => {
		return setting.minWindowWidth + ":" + setting.nItems;
	}).join(",");
}

export function getItemWidthMultipliersFromWrapAttr($wrap: JQuery): ItemWidthMultiplier[] {
	const attrVal: string = $wrap.attr('data-item-width-multipliers') || '';
	if (!attrVal) {
		return [];
	}
	const data = safeParseJSON(attrVal);
	if (!data?.length) {
		return [];
	}
	return map(data, (item: number[]) => ({ minWindowWidth: item[0], multiplier: item[1] }));
}

export function getItemWidthMultiplier($window: JQuery<Window & typeof globalThis>, itemWidthMultipliers: ItemWidthMultiplier[]): number {
	if (itemWidthMultipliers.length === 0) {
		return 1;
	}
	let itemWidthMultiplier: number = 1;
	const w: number = $window.outerWidth() || 0;
	each(itemWidthMultipliers, cfg => {
		if (w < cfg.minWindowWidth) {
			return false;
		}
		itemWidthMultiplier = cfg.multiplier;
	});
	return itemWidthMultiplier;
}

export function highlightGroupControl($wrap: JQuery, groupIndex: number): void {
	const wrapSelector = '.dot-controls,.number-controls';
	const $ctrlWrap = $wrap.find(wrapSelector).add($wrap.filter(wrapSelector));
	const $controls = $ctrlWrap.find('.dot,.number');
	$controls.removeClass('selected').filter(function () {
		return $(this).index() === groupIndex;
	}).addClass('selected');
}
